import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, IconButton, Paper,
  Table, TableHead, TableRow, TableCell, TableBody,
  Divider, CircularProgress,
} from "@mui/material";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon     from "@mui/icons-material/CancelOutlined";
import HourglassEmptyIcon     from "@mui/icons-material/HourglassEmpty";
import api from "../../services/api";

/* ─── helpers ─── */
function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
}

/* ─── status config (overall) ─── */
const STATUS_CFG = {
  pending:   { label: "Pending",   color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,      desc: "Pengajuan ATK sedang menunggu persetujuan dari admin." },
  disetujui: { label: "Disetujui", color: "#059669", bg: "#f0fdf4", border: "#bbf7d0", icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,   desc: "Pengajuan ATK telah disetujui oleh admin." },
  ditolak:   { label: "Ditolak",   color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: <CancelOutlinedIcon sx={{ fontSize: 14 }} />,        desc: "Pengajuan ATK ditolak oleh admin." },
  dibeli:    { label: "Dibeli",    color: "#059669", bg: "#f0fdf4", border: "#bbf7d0", icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,   desc: "Pengajuan ATK telah selesai dibeli." },
};

/* ─── status config (item) ─── */
const ITEM_STATUS_CFG = {
  pending:    { label: "Pending",      color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  dibeli:     { label: "Dibeli",       color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" },
  belum_beli: { label: "Belum Dibeli", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};

const REQUEST_TYPE_CFG = {
  biasa:    { label: "Biasa",    color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  segera:   { label: "Segera",   color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  mendesak: { label: "Mendesak", color: "#ea580c", bg: "#fff7ed", border: "#fdba74" },
};

function getStatus(name, cfg) {
  return cfg[name?.toLowerCase()] || { label: name || "-", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
}

/* ─── InfoRow ─── */
function InfoRow({ label, value }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{
        fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af",
        textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5,
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.875rem", color: "#111827", lineHeight: 1.5 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

/* ═══ Main ═══ */
export default function UserDetailAtk() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/atk/${id}`);
        setData(res.data?.data ?? res.data);
      } catch (err) {
        setError("Gagal memuat detail pengajuan ATK.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
      <CircularProgress size={28} sx={{ color: "#2563eb" }} />
    </Box>
  );

  if (error || !data) return (
    <Box sx={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", p: 3 }}>
      <Typography sx={{ color: "#dc2626", fontSize: "0.875rem" }}>{error || "Data tidak ditemukan."}</Typography>
    </Box>
  );

  const sc    = getStatus(data.status?.name, STATUS_CFG);
  const rtCfg = REQUEST_TYPE_CFG[data.request_type?.toLowerCase()] || REQUEST_TYPE_CFG.biasa;
  const items = data.items || [];

  /* summary counts — logic ATK tetap dipertahankan */
  const countDibeli    = items.filter(i => i.item_status === "dibeli").length;
  const countBelumBeli = items.filter(i => i.item_status === "belum_beli").length;
  const countPending   = items.filter(i => !i.item_status || i.item_status === "pending").length;

  return (
    <Box>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} size="small" sx={{
          border: "1px solid #e5e7eb", borderRadius: "8px", width: 34, height: 34,
          color: "#6b7280",
          "&:hover": { borderColor: "#2563eb", color: "#2563eb", backgroundColor: "#eff6ff" },
        }}>
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>
            Detail Pengajuan ATK
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af", mt: 0.2 }}>
            {data.request_number || "-"}
          </Typography>
        </Box>
        {/* Status badge */}
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: 0.6,
          px: 1.5, py: "5px", borderRadius: "8px",
          backgroundColor: sc.bg, border: `1px solid ${sc.border}`,
        }}>
          <Box sx={{ color: sc.color, display: "flex", alignItems: "center" }}>{sc.icon}</Box>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: sc.color }}>
            {sc.label}
          </Typography>
        </Box>
      </Box>

      {/* ── Status Banner ── */}
      <Box sx={{
        backgroundColor: sc.bg, border: `1px solid ${sc.border}`,
        borderRadius: "10px", px: 2, py: 1.5, mb: 3,
        display: "flex", alignItems: "center", gap: 1,
      }}>
        <Box sx={{ color: sc.color, display: "flex", alignItems: "center" }}>{sc.icon}</Box>
        <Typography sx={{ fontSize: "0.845rem", color: sc.color, fontWeight: 500 }}>
          {sc.desc}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 2.5, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>

        {/* ── Kolom Kiri — Info ── */}
        <Paper elevation={0} sx={{
          border: "1px solid #e5e7eb", borderRadius: "12px", p: 3,
          flex: "0 0 260px", width: { xs: "100%", md: 260 },
        }}>
          <Typography sx={{
            fontSize: "0.78rem", fontWeight: 700, color: "#6b7280",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 2.5,
          }}>
            Informasi Pengajuan
          </Typography>

          <InfoRow label="No. Pengajuan"     value={data.request_number} />
          <InfoRow label="Tanggal"           value={formatDate(data.request_date)} />
          <InfoRow label="Nama Pemohon"      value={data.requester_name} />
          <InfoRow label="Departemen / Unit" value={data.department} />
          {data.used_for && <InfoRow label="Digunakan Untuk" value={data.used_for} />}

          {/* Sifat Permintaan */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{
              fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af",
              textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75,
            }}>
              Sifat Permintaan
            </Typography>
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              px: 1.25, py: "4px", borderRadius: "6px",
              backgroundColor: rtCfg.bg, border: `1px solid ${rtCfg.border}`,
            }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: rtCfg.color }}>
                {rtCfg.label}
              </Typography>
            </Box>
          </Box>

          <InfoRow label="Diajukan Oleh" value={data.user?.name} />

          {data.description && (
            <Box>
              <Typography sx={{
                fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af",
                textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75,
              }}>
                Catatan
              </Typography>
              <Box sx={{
                backgroundColor: "#f8fafc", border: "1px solid #e5e7eb",
                borderRadius: "8px", p: 1.5,
              }}>
                <Typography sx={{ fontSize: "0.845rem", color: "#374151", lineHeight: 1.6 }}>
                  {data.description}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>

        {/* ── Kolom Kanan — Daftar Item ATK ── */}
        <Paper elevation={0} sx={{
          border: "1px solid #e5e7eb", borderRadius: "12px", p: 3, flexGrow: 1,
          minWidth: 0,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
            <Typography sx={{
              fontSize: "0.78rem", fontWeight: 700, color: "#6b7280",
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}>
              Daftar Item ATK
            </Typography>
            <Box sx={{
              px: "8px", height: 20, borderRadius: "5px",
              backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
              display: "inline-flex", alignItems: "center",
            }}>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#2563eb" }}>
                {items.length} item
              </Typography>
            </Box>
          </Box>

          {/* Desktop Table View */}
          <Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  {["No", "Nama Item ATK", "Qty", "Satuan", "Status Item"].map((h) => (
                    <TableCell key={h} sx={{
                      fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af",
                      textTransform: "uppercase", letterSpacing: "0.05em",
                      borderBottom: "1px solid #e5e7eb", py: 1.2, whiteSpace: "nowrap",
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 5 }}>
                      <Typography sx={{ color: "#9ca3af", fontSize: "0.845rem" }}>
                        Tidak ada item
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : items.map((item, idx) => {
                  const isc = getStatus(item.item_status, ITEM_STATUS_CFG);
                  return (
                    <TableRow key={item.id || idx} sx={{
                      "&:hover": { backgroundColor: "#fafafa" },
                      borderBottom: "1px solid #f3f4f6",
                    }}>
                      <TableCell sx={{ color: "#9ca3af", fontSize: "0.78rem", width: 36 }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: "0.845rem", fontWeight: 600, color: "#111827" }}>
                          {item.item_name || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.845rem", color: "#374151", whiteSpace: "nowrap" }}>
                        {item.quantity}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.845rem", color: "#374151", whiteSpace: "nowrap" }}>
                        {item.satuan || "-"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{
                          display: "inline-flex", alignItems: "center",
                          px: 1, py: "3px", borderRadius: "5px",
                          backgroundColor: isc.bg, border: `1px solid ${isc.border}`,
                        }}>
                          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: isc.color }}>
                            {isc.label}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 2 }}>
            {items.length === 0 ? (
              <Typography sx={{ color: "#9ca3af", fontSize: "0.845rem", textAlign: "center", py: 4 }}>
                Tidak ada item
              </Typography>
            ) : items.map((item, idx) => {
              const isc = getStatus(item.item_status, ITEM_STATUS_CFG);
              return (
                <Box key={item.id || idx} sx={{
                  p: 2, borderRadius: "10px", border: "1px solid #f3f4f6",
                  backgroundColor: "#fff",
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827" }}>
                      {item.item_name || "-"}
                    </Typography>
                    <Box sx={{
                      px: 1, py: "2px", borderRadius: "4px",
                      backgroundColor: isc.bg, border: `1px solid ${isc.border}`,
                    }}>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: isc.color }}>
                        {isc.label}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af", textTransform: "uppercase", fontWeight: 700 }}>Qty</Typography>
                      <Typography sx={{ fontSize: "0.845rem", fontWeight: 600 }}>{item.quantity}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af", textTransform: "uppercase", fontWeight: 700 }}>Satuan</Typography>
                      <Typography sx={{ fontSize: "0.845rem", fontWeight: 600 }}>{item.satuan || "-"}</Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Summary — style Divider + row bawah, sama seperti Procurement */}
          {items.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>

                {/* Kiri: breakdown status item */}
                <Box sx={{ display: "flex", gap: 3 }}>
                  {[
                    { label: "Sudah Dibeli", value: countDibeli,    color: "#059669" },
                    { label: "Belum Dibeli", value: countBelumBeli, color: "#dc2626" },
                    { label: "Pending",      value: countPending,   color: "#d97706" },
                  ].map((s) => (
                    <Box key={s.label}>
                      <Typography sx={{
                        fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af",
                        textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.2,
                      }}>
                        {s.label}
                      </Typography>
                      <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: s.color, lineHeight: 1 }}>
                        {s.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Kanan: Total item — mengikuti style "Total Pengajuan" di Procurement */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography sx={{ fontSize: "0.845rem", color: "#6b7280", fontWeight: 500 }}>
                    Total Item:
                  </Typography>
                  <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em" }}>
                    {items.length}
                  </Typography>
                </Box>

              </Box>
            </>
          )}
        </Paper>

      </Box>
    </Box>
  );
}
